// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages } from "react-intl";
import { Button } from "react-bootstrap";

import { IState } from "states/states";
import { IAction, ActionType, raiseAction, IActionTypeChangeAwsUser } from "actions";
import { DataState } from "types";
import { isSet } from "shared/utils/utils";
import { MIN_PASSWORD_LENGTH } from "guiutils";

import InfoScreen from "view/comp/InfoScreen";
import InfoRow from "view/comp/InfoRow";
import InputField, { eInputFieldType } from "view/comp/InputField";
import UserMfaSettings from "./UserMfaSettings";

import IconUser from "icons/navUser";
import IconEmail from "icons/iconEmail";
import IconLocked from "icons/locked";

// uses webpack definitions
declare const IS_CLOUD: boolean;

interface IUserPwdChangeProps {
  secondaryId: string;
  realName: string | undefined;
  email: string | undefined;
  dataState: DataState;
  saveResult: string | undefined;
}

const mapStateToProps = (state: IState) => {
  const props: IUserPwdChangeProps = {
    secondaryId: state.site.user?.secId ?? "",
    realName: state.site.user?.realName,
    email: state.site.user?.email,
    dataState: state.scheduler.loginDataState,
    saveResult: state.gui.login.userStateErrorInfo,
  };
  return props;
};

interface IUserPwdChangeState {
  oldPassword: string;
  newPassword: string;
  newPassword2: string;
  realName: string;
  showNameError: boolean;
  showPasswordError: boolean;
}

interface IUserPwdChangeDispatch {
  onChangePassword: (
    secId: string,
    oldPassword: string,
    newPassword: string,
    name: string | undefined,
  ) => void;
  onCancel: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IUserPwdChangeDispatch = {
    onChangePassword: (
      secId: string,
      oldPassword: string,
      newPassword: string,
      name: string | undefined,
    ) => {
      const payload: IActionTypeChangeAwsUser = {
        secondaryId: secId,
        oldPassword,
        newPassword,
        realName: name,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_AWSUSER_REQ, payload));
    },
    onCancel: () => {
      dispatch(raiseAction(ActionType.ACT_CANCEL_PWD_CHANGE));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  oldPasswordPlaceholder: { id: "Please enter your current password." },
  passwordPlaceholder: { id: "min. 8 characters/digits/symbols" },
});

class UserPwdChangeComponent extends React.Component<
  IUserPwdChangeProps & IUserPwdChangeDispatch,
  IUserPwdChangeState
> {
  state: IUserPwdChangeState = {
    oldPassword: "",
    newPassword: "",
    newPassword2: "",
    realName: "",
    showNameError: false,
    showPasswordError: false,
  };

  public render() {
    const doDataCompletion = this.props.realName === undefined;

    return (
      <div className="infoScreenPage">
        <InfoScreen
          title={
            doDataCompletion ?
              <FormattedMessage id="Complete user data" />
            : <FormattedMessage id="Modify current user" />
          }
        >
          <form
            onSubmit={(event) => {
              event.preventDefault();
              this.changePassword();
            }}
          >
            <table>
              <tbody>
                {doDataCompletion ?
                  <InfoRow
                    icon={<IconUser />}
                    label={<FormattedMessage id="Full name" />}
                    labelFor="realName"
                    value={
                      <InputField
                        inputType={eInputFieldType.Text}
                        name="realName"
                        value={this.state.realName}
                        dataState={this.props.dataState}
                        autoFocus
                        errorText={
                          (
                            (this.props.dataState === DataState.SAVE_FAIL ||
                              this.state.showNameError) &&
                            !this.isNameValid()
                          ) ?
                            "User name needed!"
                          : undefined
                        }
                        onChange={(event) =>
                          this.setState({ realName: event?.currentTarget.value })
                        }
                      />
                    }
                  />
                : <InfoRow
                    icon={<IconEmail />}
                    label={<FormattedMessage id="Email" />}
                    value={this.props.email}
                  />
                }
                {!doDataCompletion && (
                  <InfoRow
                    icon={<IconLocked />}
                    label={<FormattedMessage id="Old password" />}
                    labelFor="oldPassword"
                    value={
                      <InputField
                        inputType={eInputFieldType.Password}
                        name="oldPassword"
                        autoComplete="current-password"
                        value={this.state.oldPassword}
                        dataState={this.props.dataState}
                        placeholder={messages.oldPasswordPlaceholder}
                        autoFocus
                        errorText={
                          (
                            (this.props.dataState === DataState.SAVE_FAIL ||
                              this.state.showPasswordError) &&
                            this.state.oldPassword.length === 0
                          ) ?
                            "Password needed"
                          : undefined
                        }
                        onChange={(event) =>
                          this.setState({ oldPassword: event?.currentTarget.value })
                        }
                      />
                    }
                  />
                )}
                <InfoRow
                  icon={<IconLocked />}
                  label={<FormattedMessage id="New password" />}
                  labelFor="newPassword"
                  value={
                    <InputField
                      inputType={eInputFieldType.Password}
                      name="newPassword"
                      autoComplete="new-password"
                      value={this.state.newPassword}
                      dataState={this.props.dataState}
                      placeholder={messages.passwordPlaceholder}
                      errorText={
                        (
                          this.props.dataState === DataState.SAVE_FAIL ||
                          this.state.showPasswordError
                        ) ?
                          this.state.newPassword.length === 0 ?
                            "Password needed"
                          : this.state.newPassword.length < MIN_PASSWORD_LENGTH ?
                            "Password too short"
                          : this.state.newPassword !== this.state.newPassword2 ?
                            "Passwords are different"
                          : undefined
                        : undefined
                      }
                      onChange={(event) =>
                        this.setState({ newPassword: event?.currentTarget.value })
                      }
                    />
                  }
                />
                <InfoRow
                  icon={<IconLocked />}
                  label={<FormattedMessage id="Verify new password" />}
                  labelFor="newPassword2"
                  value={
                    <InputField
                      inputType={eInputFieldType.Password}
                      name="newPassword2"
                      autoComplete="new-password"
                      value={this.state.newPassword2}
                      dataState={this.props.dataState}
                      placeholder={messages.passwordPlaceholder}
                      errorText={
                        (
                          this.props.dataState === DataState.SAVE_FAIL ||
                          this.state.showPasswordError
                        ) ?
                          this.state.newPassword2.length === 0 ?
                            "Password needed"
                          : this.state.newPassword2.length < MIN_PASSWORD_LENGTH ?
                            "Password too short"
                          : this.state.newPassword !== this.state.newPassword2 ?
                            "Passwords are different"
                          : undefined
                        : undefined
                      }
                      onChange={(event) =>
                        this.setState({ newPassword2: event?.currentTarget.value })
                      }
                    />
                  }
                />
              </tbody>
            </table>
            <div className="buttonbox">
              <Button
                className="infoScreenButton"
                variant="primary"
                id="userpwdchange"
                type="submit"
                disabled={this.props.dataState === DataState.SAVE_REQ}
              >
                {doDataCompletion ?
                  <FormattedMessage id="Finish User Setup" />
                : <FormattedMessage id="Change Password" />}
              </Button>
              {!doDataCompletion && (
                <Button
                  className="infoScreenButton"
                  variant="secondary"
                  type="button"
                  onClick={() => this.props.onCancel()}
                  disabled={this.props.dataState === DataState.SAVE_REQ}
                >
                  <FormattedMessage id="Cancel" />
                </Button>
              )}
            </div>
          </form>
          {this.props.dataState === DataState.SAVE_REQ && (
            <div className="alert alert-warning mt-3">
              <FormattedMessage id="Changing password..." />
            </div>
          )}
          {this.props.dataState === DataState.SAVE_FAIL && (
            <div className="alert alert-danger mt-3">
              <FormattedMessage id="Changing password failed." />
              <br />
              {isSet(this.props.saveResult) && <FormattedMessage id={this.props.saveResult} />}
            </div>
          )}
          {IS_CLOUD && !doDataCompletion && <UserMfaSettings />}
        </InfoScreen>
      </div>
    );
  }

  private isNameValid() {
    return this.state.realName.trim().length > 0;
  }

  private isOldPasswordValid() {
    return this.state.oldPassword.length > 0;
  }

  private isNewPasswordValid() {
    return (
      this.state.newPassword.length >= MIN_PASSWORD_LENGTH &&
      this.state.newPassword === this.state.newPassword2
    );
  }

  private changePassword() {
    const doDataCompletion = this.props.realName === undefined;
    const nameOk = !doDataCompletion || this.isNameValid();
    const oldPasswordOk = doDataCompletion || this.isOldPasswordValid();

    if (!nameOk || !oldPasswordOk || !this.isNewPasswordValid()) {
      this.setState({ showNameError: true, showPasswordError: true });
      return;
    }

    this.props.onChangePassword(
      this.props.secondaryId,
      this.state.oldPassword,
      this.state.newPassword,
      doDataCompletion ? this.state.realName : undefined,
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(UserPwdChangeComponent);
